import { StarRate } from '@mui/icons-material'
import { Box, CircularProgress, Paper, Rating, Stack, Typography } from '@mui/material'
import { format } from 'date-fns'

import { useScoutOsaRatingsQuery } from '../../api'
import { SCOUT_RATING_CATEGORIES } from '../../types'

interface ScoutRatingsSectionProps {
  appointmentId: string
}

export const ScoutRatingsSection = ({ appointmentId }: ScoutRatingsSectionProps) => {
  const { data, isLoading, isError } = useScoutOsaRatingsQuery(appointmentId)
  const ratings = data?.data ?? []

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: theme => theme.palette.text.primary,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
        }}
      >
        <Box
          sx={{
            width: 4,
            height: 24,
            backgroundColor: theme => theme.palette.primary.main,
            borderRadius: 1,
          }}
        />
        Scout Ratings
      </Typography>

      {isLoading && (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading ratings...
          </Typography>
        </Box>
      )}

      {isError && (
        <Typography variant="body2" color="error.main">
          Failed to load ratings
        </Typography>
      )}

      {!isLoading && !isError && ratings.length === 0 && (
        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No ratings yet
          </Typography>
        </Paper>
      )}

      {ratings.length > 0 && (
        <Stack spacing={2}>
          {ratings.map(rating => (
            <Paper key={rating.uuid} variant="outlined" sx={{ p: 2 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
                mb={1.5}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <StarRate color="action" fontSize="small" />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {rating.scout_name || rating.scout_uuid}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Rated by {rating.user_email}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {format(new Date(rating.created_at), 'PP HH:mm')}
                </Typography>
              </Box>

              <Stack spacing={1}>
                {SCOUT_RATING_CATEGORIES.map(category => (
                  <Box
                    key={category.field}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {category.label}
                    </Typography>
                    <Rating value={rating[category.field]} readOnly size="small" />
                  </Box>
                ))}
              </Stack>

              {rating.note && (
                <Typography variant="body2" sx={{ mt: 1.5, whiteSpace: 'pre-wrap' }}>
                  {rating.note}
                </Typography>
              )}
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  )
}
